/**
 * Lightweight error reporter.
 *
 * Called from every `catch` that used to be a bare `catch {}` and silently
 * lost information. Does two things:
 *
 *  1. `console.error` with a `[specforma:error]` prefix + caller-supplied
 *     context label so the console tells you WHICH load/save failed and
 *     not just "something threw."
 *
 *  2. Dispatches a `specforma-error` CustomEvent on `window` carrying
 *     `{ ctx, message, at }`. A future toast component (Phase D.5) will
 *     subscribe and surface these to the user; until then this is a no-op
 *     for the UI, but the hook is in place so we don't have to touch every
 *     call-site again later.
 *
 * Usage:
 *   try { ... } catch (err) { reportError('app:loadOrgs', err); }
 */
export function reportError(ctx: string, err: unknown): void {
  const message = err instanceof Error ? err.message : String(err);
  const stack = err instanceof Error ? err.stack : undefined;

  console.error(`[specforma:error] ${ctx}:`, stack || message);
  try {
    window.dispatchEvent(
      new CustomEvent('specforma-error', {
        detail: { ctx, message, at: Date.now() },
      })
    );
  } catch {
    /* event dispatch itself must never throw */
  }
}
